import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Programs', path: '/programs' },
    { name: 'Testimonies', path: '/testimonies' },
    { name: 'The Quist Foundation', path: '/foundation' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 sm:gap-3 cursor-pointer flex-shrink-0"
            aria-label="Praying Women Connect - Home"
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/abcfde42a70d25526c2b82811ba21d87/cf412d9fb78223cea549cff9d81f857f.png"
                alt="PWC Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="hidden sm:block">
              <div className={`font-serif font-bold text-base lg:text-lg leading-tight ${isScrolled ? 'text-rose-800' : 'text-white'}`}>
                Praying Women Connect
              </div>
              <div className={`text-xs ${isScrolled ? 'text-amber-600' : 'text-amber-300'}`}>
                Connecting With Jesus
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-semibold text-sm xl:text-base transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                  location.pathname === link.path
                    ? isScrolled
                      ? 'text-rose-800'
                      : 'text-amber-400'
                    : isScrolled
                    ? 'text-gray-700 hover:text-rose-800'
                    : 'text-white hover:text-amber-300'
                }`}
                aria-label={`Navigate to ${link.name}`}
                aria-current={location.pathname === link.path ? 'page' : undefined}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <Link
            to="/prayer-request"
            className="hidden lg:block px-4 xl:px-6 py-2.5 xl:py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm xl:text-base font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
            aria-label="Submit a prayer request"
          >
            Prayer Request
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          id="mobile-menu"
          className="lg:hidden bg-white shadow-lg"
          role="menu"
        >
          <div className="px-4 py-6 space-y-4 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-2 font-semibold text-base transition-colors duration-300 cursor-pointer ${
                  location.pathname === link.path
                    ? 'text-rose-800'
                    : 'text-gray-700 hover:text-rose-800'
                }`}
                role="menuitem"
                aria-label={`Navigate to ${link.name}`}
                aria-current={location.pathname === link.path ? 'page' : undefined}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/prayer-request"
              className="block w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-full text-center hover:from-amber-600 hover:to-amber-700 transition-all duration-300 cursor-pointer"
              role="menuitem"
              aria-label="Submit a prayer request"
            >
              Prayer Request
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}